import React, { FC, useState, useContext } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  StyleProp,
  TextStyle,
} from "react-native";
import StyledIcon from "../../components/StyledIcon";
import Colors from "../../constants/Colors";
import FontStyles from "../../constants/FontStyle";
import { AuthContext } from "../../contexts/auth/Store";
import useFetch from "../../hooks/useFetch";
import { IBuyer } from "../../interfaces/buyer";
import { ILedger, LedgerHead, LedgerSubhead } from "../../interfaces/ledger";
import { ledgerService } from "../../services/LedgerService";
import { formatCurrency } from "../../utils/money";
import { BalanceNotificationCard } from "./BalanceNotificationCard";
import { VirtualAccount } from "./VirtualAccount";

const IconText: FC<{
  icon: string;
  text: string;
  color: string;
  size?: number;
  textStyle?: StyleProp<TextStyle>;
}> = ({ icon, text, color, size, textStyle }) => {
  return (
    <View style={styles.iconTextContainer}>
      <StyledIcon
        iconName={icon}
        iconColor={color}
        size={size || 14}
        iconOnly
        iconLibrary="MaterialCommunityIcons"
      />
      <Text
        style={[
          styles.iconTextContent,
          textStyle,
          size ? { fontSize: size } : undefined,
        ]}
      >
        {text}
      </Text>
    </View>
  );
};

export const BalanceDetailsCard = () => {
  const auth = useContext(AuthContext);
  const buyer: IBuyer = auth.state.buyerData;

  const [showDetail, setShowDetail] = useState(false);

  const toggleDetail = (): void => {
    setShowDetail(!showDetail);
  };

  const [ledger, loadingLedger] = useFetch<ILedger>(
    ledgerService,
    "fetchByOwnerSpecific",
    [
      {
        head: LedgerHead.Buyer,
        subhead: LedgerSubhead.Wallet,
        owner: buyer?._id,
      },
    ],
    !!buyer?._id
  );

  return (
    <>
      <View style={styles.container}>
        <View style={styles.balanceContainer}>
          <Text style={styles.label}>Balance</Text>
          <Text
            style={[
              styles.macroTitle,
              ledger && ledger.balance < 0
                ? { color: Colors.dangerText }
                : { color: Colors.successText },
            ]}
          >
            {loadingLedger
              ? "..."
              : ledger
              ? formatCurrency(ledger?.balance)
              : `Unable to load.`}
          </Text>
        </View>

        {false && (
          <View
            style={[
              styles.detailContainer,
              showDetail
                ? {
                    paddingTop: 20,
                  }
                : undefined,
            ]}
          >
            {showDetail && <VirtualAccount />}

            <TouchableOpacity
              style={[
                styles.toggleButton,
                showDetail
                  ? {
                      marginTop: 20,
                    }
                  : undefined,
              ]}
              onPress={toggleDetail}
            >
              <IconText
                icon={showDetail ? "arrow-collapse" : "arrow-expand"}
                text={
                  showDetail ? "Hide Transfer Details" : "Show Transfer Details"
                }
                color={Colors.tintColor}
                textStyle={{
                  ...FontStyles.size14,
                  ...FontStyles.lh18,
                  ...FontStyles.medium,
                }}
                size={14}
              />
            </TouchableOpacity>
          </View>
        )}

        <BalanceNotificationCard />
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "#eef4fe",
    borderRadius: 8,
    overflow: "hidden",
    shadowColor: "rgba(35, 203, 253, 0.1)",
    shadowRadius: 0,
    shadowOffset: { height: 6, width: 6 },
    marginBottom: 20,
  },
  balanceContainer: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 14,
  },
  detailContainer: {
    backgroundColor: "rgba(233, 250, 255, 0.6)",
    borderRadius: 8,
    paddingHorizontal: 20,
  },
  iconTextContainer: {
    flexDirection: "row",
    alignItems: "center",
    alignContent: "center",
    justifyContent: "center",
  },
  iconTextContent: {
    color: Colors.tintColor,
    paddingLeft: 6,
  },
  label: {
    ...FontStyles.size13,
    ...FontStyles.lh16,
    ...FontStyles.gray,
    ...FontStyles.medium,
  },
  macroTitle: {
    ...FontStyles.size18,
    ...FontStyles.lh22,
    ...FontStyles.bold,
    paddingBottom: 14,
  },
  toggleButton: {
    padding: 12,
  },
});
